import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { useRBAC } from '../contexts/RBACContext';
import { CACHE_TTL, QUERY_KEYS } from '../providers/QueryProvider';
import logger from '../utils/logger';

/**
 * Hook for fetching dashboard data from the database
 * Uses React Query for intelligent caching - no refetch on page navigation
 * if data is still fresh (within staleTime TTL).
 * 
 * RBAC: Automatically filters by assigned campaigns for non-admin users
 */
export function useDashboardData() {
  const { assignedCampaigns, isAdminOrHigher } = useRBAC();
  const queryClient = useQueryClient();
  const [timelineFilter, setTimelineFilterState] = useState('current_month');

  // Get campaigns filter based on RBAC
  const getCampaignsFilter = useCallback(() => {
    if (isAdminOrHigher()) {
      return null;
    }
    return assignedCampaigns || [];
  }, [assignedCampaigns, isAdminOrHigher]);

  const campaignsFilter = getCampaignsFilter();

  // Main dashboard data query with React Query caching
  const {
    data: dashboardData,
    isLoading: loading,
    error: queryError,
    dataUpdatedAt
  } = useQuery({
    queryKey: [...QUERY_KEYS.DASHBOARD, timelineFilter, JSON.stringify(campaignsFilter)],
    queryFn: async () => {
      logger.log('📊 [Cache] Fetching dashboard data...');

      // Fetch main dashboard data (lead-based stats)
      const { data, error: rpcError } = await supabase.rpc('get_dashboard_complete', {
        p_campaigns: campaignsFilter,
        p_timeline_period: timelineFilter
      });

      if (rpcError) {
        logger.error('Error fetching dashboard data:', rpcError);
        throw new Error(rpcError.message);
      }

      // Fetch automation stats separately from automation tables
      let filteredAutomationStats = null;
      try {
        const { data: autoStats, error: autoError } = await supabase.rpc('get_dashboard_automation_stats', {
          p_campaigns: campaignsFilter
        });
        if (!autoError && autoStats) {
          filteredAutomationStats = autoStats;
        } else if (autoError) {
          logger.warn('get_dashboard_automation_stats not available:', autoError.message);
        }
      } catch (autoErr) {
        logger.warn('Automation stats RPC fallback:', autoErr.message);
      }

      // Fetch zero opens count
      let zeroOpensCount = 0;
      try {
        const { data: zeroOpens, error: zeroOpensError } = await supabase.rpc('get_zero_opens_count', {
          p_campaigns: campaignsFilter
        });
        if (!zeroOpensError && zeroOpens !== null) {
          zeroOpensCount = zeroOpens;
        }
      } catch (zeroErr) {
        logger.warn('get_zero_opens_count not available:', zeroErr.message);
      }

      // Fetch today's activity stats with valid reply counting
      let todayActivityStats = null;
      try {
        const { data: todayStats, error: todayError } = await supabase.rpc('get_today_activity_stats', {
          p_campaigns: campaignsFilter
        });
        if (!todayError && todayStats && todayStats.length > 0) {
          todayActivityStats = todayStats[0];
        }
      } catch (todayErr) {
        logger.warn('get_today_activity_stats not available:', todayErr.message);
      }

      // Fetch automation timeline (last 7 days) filtered by campaign
      let filteredAutomationTimeline = null;
      try {
        const { data: timelineData, error: timelineError } = await supabase.rpc('get_automation_timeline', {
          p_campaigns: campaignsFilter
        });
        if (!timelineError && timelineData && timelineData.length > 0) {
          filteredAutomationTimeline = timelineData;
        }
      } catch (timelineErr) {
        logger.warn('get_automation_timeline not available:', timelineErr.message);
      }

      if (data) {
        const mergedData = {
          ...data,
          ...(filteredAutomationStats && { automationStats: filteredAutomationStats }),
          ...(filteredAutomationTimeline && { automationTimeline: filteredAutomationTimeline }),
          zeroOpensCount,
          todayActivityStats
        };
        logger.log('✅ [Cache] Dashboard data loaded:', new Date().toISOString());
        return mergedData;
      }

      return null;
    },
    staleTime: CACHE_TTL.DASHBOARD,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,       // Refetch if stale when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchInterval: 300000,     // Background poll every 5 minutes
  });

  // Real-time subscriptions: invalidate cache (mark stale) on DB changes
  // This is more efficient than immediate refetch - data refetches only when visible
  useEffect(() => {
    const channel = supabase
      .channel('dashboard-cache-invalidation')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' },
        () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lead_events' },
        () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_automation_executions' },
        () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD }))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_automation_sequences' },
        () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD }))
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  // Refresh: force refetch regardless of staleness
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
    return queryClient.refetchQueries({ queryKey: QUERY_KEYS.DASHBOARD });
  }, [queryClient]);

  // Change timeline filter (triggers new query due to key change)
  const changeTimelineFilter = useCallback((newFilter) => {
    setTimelineFilterState(newFilter);
  }, []);

  // Fetch daily stats for a specific date (not cached - on-demand)
  const fetchDailyStats = useCallback(async (date) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_daily_stats_for_date', {
        p_date: date,
        p_campaigns: campaignsFilter
      });

      if (rpcError) {
        logger.error('Error fetching daily stats:', rpcError);
        return null;
      }

      if (data && data.length > 0) {
        const stats = data[0];
        return {
          date: stats.tracking_date,
          totalSent: parseInt(stats.total_sent) || 0,
          totalOpens: parseInt(stats.total_opens) || 0,
          totalReplies: parseInt(stats.total_replies) || 0,
          totalClicks: parseInt(stats.total_clicks) || 0,
          manualSent: parseInt(stats.manual_sent) || 0,
          automationSent: parseInt(stats.automation_sent) || 0,
          uniqueLeadsSent: parseInt(stats.unique_leads_sent) || 0,
          uniqueLeadsOpened: parseInt(stats.unique_leads_opened) || 0,
          uniqueLeadsReplied: parseInt(stats.unique_leads_replied) || 0,
          uniqueLeadsClicked: parseInt(stats.unique_leads_clicked) || 0
        };
      }

      return null;
    } catch (err) {
      logger.error('Error in fetchDailyStats:', err);
      return null;
    }
  }, [campaignsFilter]);

  // Fetch date range stats (not cached - on-demand)
  const fetchDateRangeStats = useCallback(async (startDate, endDate) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_stats_date_range', {
        p_start_date: startDate,
        p_end_date: endDate,
        p_campaigns: campaignsFilter
      });

      if (rpcError) {
        logger.error('Error fetching date range stats:', rpcError);
        return [];
      }

      return (data || []).map(stats => ({
        date: stats.stat_date,
        totalSent: parseInt(stats.total_sent) || 0,
        totalOpens: parseInt(stats.total_opens) || 0,
        totalReplies: parseInt(stats.total_replies) || 0,
        totalClicks: parseInt(stats.total_clicks) || 0,
        manualSent: parseInt(stats.manual_sent) || 0,
        automationSent: parseInt(stats.automation_sent) || 0,
        uniqueLeadsOpened: parseInt(stats.unique_leads_opened) || 0,
        uniqueLeadsReplied: parseInt(stats.unique_leads_replied) || 0
      }));
    } catch (err) {
      logger.error('Error in fetchDateRangeStats:', err);
      return [];
    }
  }, [campaignsFilter]);

  // Archive daily stats to permanent storage
  const archiveDailyStats = useCallback(async (date) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('archive_daily_stats_to_permanent', {
        p_date: date
      });

      if (rpcError) {
        logger.error('Error archiving daily stats:', rpcError);
        return false;
      }

      return true;
    } catch (err) {
      logger.error('Error in archiveDailyStats:', err);
      return false;
    }
  }, []);

  // Fetch recent activity for a specific date (with RBAC filtering)
  const fetchRecentActivityForDate = useCallback(async (date, limit = 50) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_recent_activity_for_date', {
        p_date: date,
        p_limit: limit,
        p_campaigns: campaignsFilter
      });

      if (rpcError) {
        logger.error('Error fetching recent activity for date:', rpcError);
        return [];
      }

      return data || [];
    } catch (err) {
      logger.error('Error in fetchRecentActivityForDate:', err);
      return [];
    }
  }, [campaignsFilter]);

  // Extract data sections from the complete payload
  const stats = dashboardData?.stats || {
    totalLeads: 0,
    callListLeads: 0,
    avgScore: 0,
    opens: 0,
    totalOpenEvents: 0,
    clicks: 0,
    replies: 0,
    totalManualSent: 0,
    totalAutomationSent: 0,
    totalSent: 0,
    campaigns: 0,
    activeCampaigns: 0,
    followUpCalls: 0,
    replyRate: 0,
    openRate: 0
  };

  const automationStats = dashboardData?.automationStats || {
    totalSequences: 0,
    activeSequences: 0,
    completedSequences: 0,
    repliedSequences: 0,
    totalEmails: 0,
    pendingExecutions: 0,
    failedExecutions: 0,
    totalExecutions: 0,
    emailOpens: 0,
    automationOpenRate: 0,
    automationReplyRate: 0,
    avgStepsCompleted: 0
  };

  const leadStatusDistribution = dashboardData?.leadStatusDistribution || [];
  const engagementTimeline = dashboardData?.engagementTimeline || [];
  const automationTimeline = dashboardData?.automationTimeline || [];
  const topCampaigns = dashboardData?.topCampaigns || [];
  const recentActivity = dashboardData?.recentActivity || [];
  const scoreDistribution = dashboardData?.scoreDistribution || [];
  const dailyStats = dashboardData?.dailyStats || null;
  const zeroOpensCount = dashboardData?.zeroOpensCount || 0;
  const todayActivityStats = dashboardData?.todayActivityStats || null;

  return {
    // Loading state
    loading,
    error: queryError?.message || null,
    lastRefresh: dataUpdatedAt ? new Date(dataUpdatedAt) : null,

    // Complete data
    dashboardData,

    // Individual sections (pre-extracted for convenience)
    stats,
    automationStats,
    leadStatusDistribution,
    engagementTimeline,
    automationTimeline,
    topCampaigns,
    recentActivity,
    scoreDistribution,
    dailyStats,
    zeroOpensCount,
    todayActivityStats,

    // Timeline filter
    timelineFilter,
    setTimelineFilter: changeTimelineFilter,

    // Actions
    refresh,
    fetchDailyStats,
    fetchDateRangeStats,
    archiveDailyStats,
    fetchRecentActivityForDate
  };
}

export default useDashboardData;
